import { Shield, Eye, Lock, Database, Loader } from 'lucide-react';
import { useLegalContent } from '../../utils/legalContent';

const iconMap: Record<string, any> = {
  'Eye': Eye,
  'Lock': Lock,
  'Database': Database,
  'Shield': Shield
};

export const PrivacyModal = () => {
  const { content, loading, error } = useLegalContent();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="p-6 text-center">
        <p className="text-status-error">Failed to load privacy policy content.</p>
        <p className="text-th-secondary text-sm mt-2">
          Please check your legal-content.json file.
        </p>
      </div>
    );
  }

  const privacy = content.privacyPolicy;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="w-6 h-6 text-brand" />
          <h3 className="text-xl font-semibold text-th-primary">
            {privacy.title}
          </h3>
        </div>
        <p className="text-th-secondary">
          {privacy.subtitle}
        </p>
      </div>

      {/* Privacy Principles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {privacy.principles.map((principle, index) => {
          const IconComponent = iconMap[principle.icon] || Shield;
          return (
            <div key={index} className="card p-4">
              <IconComponent className={`w-8 h-8 ${principle.color} mb-3`} />
              <h4 className="text-th-primary font-semibold mb-2">{principle.title}</h4>
              <p className="text-th-secondary text-sm">
                {principle.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Privacy Content */}
      <div className="card p-6 space-y-6">
        {privacy.sections.map((section, index) => (
          <div key={index}>
            <h4 className="text-lg font-semibold text-th-primary mb-3">
              {section.title}
            </h4>

            {section.subsections ? (
              <div className="space-y-3">
                {section.subsections.map((subsection, subIndex) => (
                  <div key={subIndex}>
                    <h5 className="text-th-primary font-medium mb-1">{subsection.title}</h5>
                    <p className="text-th-secondary text-sm">{subsection.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <p className="text-th-secondary leading-relaxed mb-3">
                  {section.content}
                </p>
                {section.list && (
                  <ul className="list-disc list-inside text-th-secondary space-y-1 ml-4">
                    {section.list.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                )}
                {section.security && (
                  <div className="bg-surface p-4 rounded-xl mt-3">
                    <h5 className="text-th-primary font-medium mb-2">{section.security.title}</h5>
                    <ul className="text-th-secondary text-sm space-y-1">
                      {section.security.measures.map((measure, measureIndex) => (
                        <li key={measureIndex}>• {measure}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Contact Information */}
      <div className="bg-elevated border border-subtle rounded-xl p-6">
        <h4 className="text-lg font-semibold text-th-primary mb-3">
          {privacy.contact.title}
        </h4>
        <p className="text-th-secondary mb-3">
          {privacy.contact.content}
        </p>
        <div className="text-th-primary">
          <p>Email: {privacy.contact.email}</p>
          {privacy.contact.website && (
            <p>Website: {privacy.contact.website}</p>
          )}
        </div>
      </div>
    </div>
  );
};
